import { useTranslation } from 'react-i18next'
import { useApp } from '../../context/AppContext'
import { trackEvent } from '../../lib/analytics'

function MiniToggle({
  label,
  icon,
  checked,
  onChange,
  trackKey,
}: {
  label: string
  icon: string
  checked: boolean
  onChange: () => void
  trackKey: string
}) {
  return (
    <button
      onClick={() => { onChange(); trackEvent('option_toggle', { option: trackKey, value: checked ? 'off' : 'on' }) }}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-twelve border-2 font-black text-xs uppercase tracking-wider transition-all active:scale-95 ${
        checked
          ? 'border-neonGreen text-neonGreen bg-neonGreen/10'
          : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
      }`}
    >
      <span>{icon}</span>
      <span>{label}</span>
      <span className={`ml-1 text-[9px] font-black tracking-widest ${checked ? 'text-neonGreen' : 'text-slate-600 dark:text-slate-300'}`}>
        {checked ? 'ON' : 'OFF'}
      </span>
    </button>
  )
}

export default function ControlsBar() {
  const { t } = useTranslation()
  const {
    caseSensitive,
    removeEmptyLines,
    trimWhitespace,
    sortOutput,
    toggleCaseSensitive,
    toggleRemoveEmptyLines,
    toggleTrimWhitespace,
    toggleSortOutput,
    applyRemoveLines,
    applyRemoveWords,
    applyRemoveHybrid,
  } = useApp()

  return (
    <section className="mb-10 flex flex-col gap-4 p-6 rounded-twelve clay-card">
      {/* Primary action buttons */}
      <div className="flex flex-wrap items-center justify-center gap-4">
        <button
          onClick={() => { applyRemoveLines(); trackEvent('dedup_run', { method: 'lines' }) }}
          className="px-6 py-3 bg-brand hover:brightness-110 text-white font-extrabold rounded-twelve transition-all active:scale-95 flex items-center gap-2"
        >
          <span>🗑️</span> {t('controls.removeLines')}
        </button>

        <button
          onClick={() => { applyRemoveWords(); trackEvent('dedup_run', { method: 'words' }) }}
          className="px-6 py-3 bg-neonPink hover:brightness-90 text-slate-900 font-extrabold rounded-twelve transition-all active:scale-95 flex items-center gap-2"
        >
          <span>✨</span> {t('controls.removeWords')}
        </button>

        <button
          onClick={() => { applyRemoveHybrid(); trackEvent('dedup_run', { method: 'hybrid' }) }}
          className="px-6 py-3 border-2 border-neonGreen text-neonGreen hover:bg-neonGreen hover:text-white dark:hover:text-black font-extrabold rounded-twelve transition-all active:scale-95 flex items-center gap-2"
        >
          <span>💥</span> {t('controls.removeHybrid')}
        </button>
      </div>

      {/* Option toggles */}
      <div className="flex flex-wrap items-center justify-center gap-2 pt-1 border-t border-slate-200 dark:border-slate-700">
        <MiniToggle
          icon="🔤"
          label={t('controls.caseSensitive')}
          checked={caseSensitive}
          onChange={toggleCaseSensitive}
          trackKey="case_sensitive"
        />
        <MiniToggle
          icon="🧹"
          label={t('controls.removeEmpty')}
          checked={removeEmptyLines}
          onChange={toggleRemoveEmptyLines}
          trackKey="remove_empty"
        />
        <MiniToggle
          icon="✂️"
          label={t('controls.trimWhitespace')}
          checked={trimWhitespace}
          onChange={toggleTrimWhitespace}
          trackKey="trim_whitespace"
        />
        <MiniToggle
          icon="🔤"
          label={t('controls.sortOutput')}
          checked={sortOutput}
          onChange={toggleSortOutput}
          trackKey="sort_output"
        />
      </div>
    </section>
  )
}
